import {deliveryTypes} from 'app/constant'
import * as yup from 'yup'
import {Discount, ViewOrder, viewOrder} from './OrderState'
import {financialStatusTypes} from 'app/utilities'

export type Order = {
  id: string
  orderEditId: string | any
  shippingAddressId: string | any
  paymentMethodId: string | any
  billingAddressId: string | any
  orderDeliveryMethod: string
  subtotalPrice: number | any
  shippingPrice: number | any
  totalDiscounts: number | any
  wineClubDiscounts: number | any
  totalTax: number | any
  totalPrice: number | any
  wineClubDiscountCodeId: string | any
  discountCodeId: string | any
  shippingDiscountCodeId: string | any
  shippingRateId: string | any
  shippingRate: any
  shippingAddress: any
  paymentMethod: any
  billingAddress: any
  note: string | any
  isCompliant: boolean
  isFreeShipping: boolean
  salesTax: number | any
  crv: number | any
  tipAmount: number | any
}

export const order: Order = {
  id: '',
  orderDeliveryMethod: '',
  subtotalPrice: '',
  shippingPrice: '',
  totalDiscounts: '',
  wineClubDiscounts: '',
  totalTax: '',
  shippingAddress: {},
  shippingRate: {},
  orderEditId: null,
  shippingAddressId: '',
  wineClubDiscountCodeId: '',
  discountCodeId: '',
  shippingRateId: '',
  note: '',
  isCompliant: true,
  isFreeShipping: false,
  totalPrice: function (): number {
    return (
      parseFloat(this.subtotalPrice) +
      parseFloat(this.totalTax) +
      parseFloat(this.shippingPrice) -
      parseFloat(this.totalDiscounts) -
      parseFloat(this.wineClubDiscounts)
    )
  },
  paymentMethodId: '',
  paymentMethod: {},
  billingAddress: {},
  billingAddressId: '',
  shippingDiscountCodeId: '',
  salesTax: 0,
  crv: 0,
  tipAmount: 0,
}

export type EditOrderState = {
  order: Order
  viewOrder: ViewOrder
  isFetching: boolean
  newItems: any[]
  isDirty: boolean
  isProductsEditMode: boolean
  deletedItems: any[]
  discounts: Discount | any
  compliance: {
    errors: any[]
    isComplianceChecked: boolean
  }
  isCompliantFetching: boolean
  isValidateDiscount: boolean
  isValidatingDiscount: boolean
  errors: []
  orderProcessErrors: []
  isSubmitting: boolean
  discountCodeList: []
  isCustomRate: boolean
  isDirtyDiscount: boolean
  isDirtyShipping: boolean
  isShippingAddressSame: boolean
}

export const orderEditInitialState: EditOrderState = {
  viewOrder,
  order,
  isFetching: false,
  isDirty: false,
  deletedItems: [],
  newItems: [],
  compliance: {
    errors: [],
    isComplianceChecked: false,
  },
  isCompliantFetching: false,
  discounts: {},
  isValidateDiscount: false,
  isValidatingDiscount: false,
  errors: [],
  isProductsEditMode: false,
  orderProcessErrors: [],
  isSubmitting: false,
  discountCodeList: [],
  isCustomRate: false,
  isDirtyDiscount: true,
  isDirtyShipping: false,
  isShippingAddressSame: false,
}

export default EditOrderState

export const EditOrderItemSchema = yup.object({
  variantId: yup.string().required('Please select a product'),
  quantity: yup
    .number()
    .test({
      name: 'available',
      exclusive: true,
      message: 'Inventory not available',
      test: function (value: any) {
        const {parent, createError, from}: any = this
        const pickupAvailableForAdminPortal =
          parent.pickupAvailableForAdminPortal + parent.originalQty
        const shipAvailableForAdminPortal = parent.shipAvailableForAdminPortal + parent.originalQty
        const orderDeliveryMethod = from[1]?.value?.orderDeliveryMethod
        if (orderDeliveryMethod === deliveryTypes.ship && shipAvailableForAdminPortal < value) {
          return createError({path: 'quantity', message: 'Inventory not available for shipping'})
        } else if (
          orderDeliveryMethod === deliveryTypes.pickup &&
          pickupAvailableForAdminPortal < value
        ) {
          return createError({path: 'quantity', message: 'Inventory not available for pickup'})
        }

        return true
      },
    })

    .min(1, 'Quantity must be greater than or equal to 1')
    .typeError('Quantity must be greater than or equal to 1')
    .required('Please enter a quantity'),
})

export const OrderEditSchema = yup.object().shape({
  orderDeliveryMethod: yup
    .string()
    .required('Please select a order delivery method')
    .typeError('Please select a order delivery method'),
  shippingRateId: yup.string().when(['orderDeliveryMethod', 'isFreeShipping'], {
    is: (orderDeliveryMethod: any, isFreeShipping: boolean) => {
      if (isFreeShipping && orderDeliveryMethod === deliveryTypes.ship) {
        return false
      }
      return orderDeliveryMethod === deliveryTypes.ship
    },
    then: yup
      .string()
      .required('Please select a shipping method')
      .typeError('Please select a shipping method'),
    otherwise: yup.string().notRequired().nullable(),
  }),
  shippingAddressId: yup.string().when('orderDeliveryMethod', {
    is: deliveryTypes.ship,
    then: yup
      .string()
      .required('Please select a shipping address')
      .typeError('Please select a shipping address'),
    otherwise: yup.string().notRequired().nullable(),
  }),
  items: yup
    .array()
    .of(EditOrderItemSchema)
    .min(1, 'Please select at least one product')
    .required('Please select at least one product'),
})

export const isOrderPaymentFailed = (financialStatus: string) => {
  return (
    financialStatus === financialStatusTypes.unPaid ||
    financialStatus === financialStatusTypes.paymentFailed ||
    financialStatus === financialStatusTypes.require3d
  )
}
